import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Branding from './Branding';

const GuestHelpInterface = () => {
  const { userId, sessionId } = useParams();
  const navigate = useNavigate();
  
  const [helpRequest, setHelpRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [helpContent, setHelpContent] = useState('');
  const [helperNickname, setHelperNickname] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [helpType, setHelpType] = useState('explanation');

  useEffect(() => {
    fetchHelpRequest();
  }, [userId, sessionId]);

  const fetchHelpRequest = async () => {
    try {
      const response = await axios.get(`/api/guest-help/${userId}/${sessionId}`);
      if (response.data.success) {
        setHelpRequest(response.data.help_request);
        if (response.data.help_request.already_helped) {
          setSubmitted(true);
        }
      } else {
        setError('Help request not found or expired');
      }
    } catch (err) {
      console.error('Error fetching help request:', err);
      setError('Unable to load help request. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  const submitHelp = async () => {
    if (!helpContent.trim()) {
      alert('Please enter some help content');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`/api/guest-help/${userId}/${sessionId}/submit`, {
        help_content: helpContent,
        helper_nickname: helperNickname || 'Anonymous Friend',
        help_type: helpType
      });

      if (response.data.success) {
        setSubmitted(true);
      } else {
        alert('Failed to submit help. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting help:', err);
      alert('Error submitting help. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    try {
      // Track conversion
      await axios.post(`/api/guest-help/${userId}/${sessionId}/convert`);
      // Don't release tokens yet - wait for paid upgrade
      // User gets free account first to experience value
    } catch (error) {
      console.error('Conversion tracking failed:', error);
    }
    
    // Redirect to free signup with bonus context
    window.location.href = '/login?mode=signup&source=guest_help&promo=friend_helper&bonus_pending=50';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: Branding.colors.nearlyWhite
      }}>
        <div style={{
          textAlign: 'center',
          padding: Branding.spacing.xl
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: `4px solid ${Branding.colors.vibrantOrange}`,
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: Branding.colors.slateGray }}>Loading help request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: Branding.colors.nearlyWhite,
        padding: Branding.spacing.md
      }}>
        <div style={{
          background: Branding.colors.pureWhite,
          padding: Branding.spacing.xl,
          borderRadius: Branding.borders.radius.lg,
          boxShadow: Branding.shadows.md,
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: Branding.spacing.md
          }}>😕</div>
          <h2 style={{
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.md
          }}>Oops!</h2>
          <p style={{
            color: Branding.colors.slateGray,
            marginBottom: Branding.spacing.lg
          }}>{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: Branding.colors.vibrantOrange,
              color: Branding.colors.pureWhite,
              border: 'none',
              padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
              borderRadius: Branding.borders.radius.md,
              fontSize: Branding.typography.fontSize.base,
              cursor: 'pointer',
              transition: Branding.transitions.default
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: Branding.colors.nearlyWhite,
        padding: Branding.spacing.md
      }}>
        <div style={{
          background: Branding.colors.pureWhite,
          padding: Branding.spacing.xl,
          borderRadius: Branding.borders.radius.lg,
          boxShadow: Branding.shadows.md,
          textAlign: 'center',
          maxWidth: '600px'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: Branding.spacing.lg
          }}>🎉</div>
          
          <h2 style={{
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.md,
            fontSize: Branding.typography.fontSize.xl
          }}>Help Sent!</h2>
          
          <p style={{
            color: Branding.colors.slateGray,
            marginBottom: Branding.spacing.xl,
            lineHeight: 1.6
          }}>
            Your friend will see your help right away. Thanks for being an awesome friend! 🤝
          </p>

          {/* Conversion CTA */}
          <div style={{
            background: `linear-gradient(135deg, ${Branding.colors.vibrantOrange}, ${Branding.colors.tealBlue})`,
            padding: Branding.spacing.lg,
            borderRadius: Branding.borders.radius.md,
            marginBottom: Branding.spacing.lg,
            color: Branding.colors.pureWhite
          }}>
            <h3 style={{
              marginBottom: Branding.spacing.sm,
              fontSize: Branding.typography.fontSize.lg
            }}>🪙 Bonus Alert! Want your own AI tutor?</h3>
            <p style={{
              marginBottom: Branding.spacing.md,
              opacity: 0.9
            }}>
              Start free and experience the magic! Your 50 bonus tokens (plus your study partner's) unlock when you're ready to upgrade. ✨
            </p>
            <button
              onClick={handleSignUp}
              style={{
                background: Branding.colors.pureWhite,
                color: Branding.colors.vibrantOrange,
                border: 'none',
                padding: `${Branding.spacing.sm} ${Branding.spacing.xl}`,
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.bold,
                cursor: 'pointer',
                transition: Branding.transitions.default,
                boxShadow: Branding.shadows.sm
              }}
            >
              Get Your Own AI Tutor ✨
            </button>
          </div>

          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'transparent',
              color: Branding.colors.slateGray,
              border: `1px solid ${Branding.colors.lightGray}`,
              padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
              borderRadius: Branding.borders.radius.md,
              fontSize: Branding.typography.fontSize.base,
              cursor: 'pointer',
              transition: Branding.transitions.default
            }}
          >
            Maybe Later
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: Branding.colors.nearlyWhite,
      padding: Branding.spacing.md
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: Branding.spacing.lg
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: Branding.spacing.xl
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: Branding.spacing.sm
          }}>🤝</div>
          <h1 style={{
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.sm,
            fontSize: Branding.typography.fontSize.xxl
          }}>Help a Friend</h1>
          <p style={{
            color: Branding.colors.slateGray,
            fontSize: Branding.typography.fontSize.lg
          }}>
            Your friend needs help with this problem
          </p>
        </div>

        {/* Problem Context */}
        <div style={{
          background: Branding.colors.pureWhite,
          padding: Branding.spacing.lg,
          borderRadius: Branding.borders.radius.lg,
          boxShadow: Branding.shadows.sm,
          marginBottom: Branding.spacing.xl,
          borderLeft: `4px solid ${Branding.colors.vibrantOrange}`
        }}>
          <h3 style={{
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.md,
            display: 'flex',
            alignItems: 'center',
            gap: Branding.spacing.sm
          }}>
            <span>📚</span>
            {helpRequest.problem_subject ? `${helpRequest.problem_subject.charAt(0).toUpperCase() + helpRequest.problem_subject.slice(1)} Problem` : 'Problem'}
          </h3>
          <div style={{
            background: Branding.colors.offWhite,
            padding: Branding.spacing.md,
            borderRadius: Branding.borders.radius.md,
            fontFamily: 'monospace',
            fontSize: Branding.typography.fontSize.base,
            lineHeight: 1.6,
            color: Branding.colors.darkSlate,
            whiteSpace: 'pre-wrap'
          }}>
            {helpRequest.problem_context}
          </div>
        </div>

        {/* Help Form */}
        <div style={{
          background: Branding.colors.pureWhite,
          padding: Branding.spacing.xl,
          borderRadius: Branding.borders.radius.lg,
          boxShadow: Branding.shadows.md
        }}>
          <h3 style={{
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: Branding.spacing.sm
          }}>
            <span>💡</span>
            Share Your Help
          </h3>

          {/* Helper Nickname */}
          <div style={{ marginBottom: Branding.spacing.lg }}>
            <label style={{
              display: 'block',
              color: Branding.colors.slateGray,
              fontSize: Branding.typography.fontSize.sm,
              fontWeight: Branding.typography.fontWeight.medium,
              marginBottom: Branding.spacing.sm
            }}>
              Your Name (optional)
            </label>
            <input
              type="text"
              value={helperNickname}
              onChange={(e) => setHelperNickname(e.target.value)}
              placeholder="e.g. Emma, Alex, or leave blank for Anonymous"
              style={{
                width: '100%',
                padding: Branding.spacing.md,
                border: `1px solid ${Branding.colors.lightGray}`,
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                transition: Branding.transitions.default
              }}
            />
          </div>

          {/* Help Type */}
          <div style={{ marginBottom: Branding.spacing.lg }}>
            <label style={{
              display: 'block',
              color: Branding.colors.slateGray,
              fontSize: Branding.typography.fontSize.sm,
              fontWeight: Branding.typography.fontWeight.medium,
              marginBottom: Branding.spacing.sm
            }}>
              Type of Help
            </label>
            <select
              value={helpType}
              onChange={(e) => setHelpType(e.target.value)}
              style={{
                width: '100%',
                padding: Branding.spacing.md,
                border: `1px solid ${Branding.colors.lightGray}`,
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                background: Branding.colors.pureWhite
              }}
            >
              <option value="explanation">Step-by-step explanation</option>
              <option value="hint">Just a hint</option>
              <option value="encouragement">Encouragement & support</option>
              <option value="text">General help</option>
            </select>
          </div>

          {/* Help Content */}
          <div style={{ marginBottom: Branding.spacing.xl }}>
            <label style={{
              display: 'block',
              color: Branding.colors.slateGray,
              fontSize: Branding.typography.fontSize.sm,
              fontWeight: Branding.typography.fontWeight.medium,
              marginBottom: Branding.spacing.sm
            }}>
              Your Help *
            </label>
            <textarea
              value={helpContent}
              onChange={(e) => setHelpContent(e.target.value)}
              placeholder="Share your explanation, hints, or encouragement here..."
              rows={6}
              style={{
                width: '100%',
                padding: Branding.spacing.md,
                border: `1px solid ${Branding.colors.lightGray}`,
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                lineHeight: 1.6,
                resize: 'vertical',
                transition: Branding.transitions.default
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={submitHelp}
            disabled={submitting || !helpContent.trim()}
            style={{
              width: '100%',
              background: helpContent.trim() ? Branding.colors.vibrantOrange : Branding.colors.lightGray,
              color: Branding.colors.pureWhite,
              border: 'none',
              padding: Branding.spacing.lg,
              borderRadius: Branding.borders.radius.md,
              fontSize: Branding.typography.fontSize.lg,
              fontWeight: Branding.typography.fontWeight.bold,
              cursor: helpContent.trim() ? 'pointer' : 'not-allowed',
              transition: Branding.transitions.default,
              boxShadow: helpContent.trim() ? Branding.shadows.md : 'none'
            }}
          >
            {submitting ? 'Sending Help...' : 'Send Help 🚀'}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GuestHelpInterface;